import { Box, Divider, Group, SimpleGrid, Slider, Stack, Text, Title, Tooltip } from '@mantine/core';
import { BNButton } from '../../components/Button/Button';
import { BNNumberInput } from '../../components/NumberInput/NumberInput';
import { BNSelect } from '../../components/Select/Select';
import { BNTextInput } from '../../components/TextInput/TextInput';
import { useState } from 'react';
import { AdminContentHeader } from '../../hoc/Admin/AdminContentHeader';
import {
  BrokerNerdsBarkerCoreEnumsAdjustmentType,
  BrokerNerdsBarkerCoreEnumsDeliveryMethod,
  BrokerNerdsBarkerCoreEnumsListingDisclosure,
  BrokerNerdsBarkerCoreEnumsMarketplace,
  BrokerNerdsBarkerCoreEnumsPricingMode,
} from '../../api';
import { useFlag } from '@unleash/proxy-client-react';
import { StaggerByValueInput } from '../../components/AdjustmentInput/StaggerByValueInput';
import { BNSwitch } from '../../components/Switch/Switch';
import { AdjustmentValueInputDiagonal } from '../../components/AdjustmentInput/AdjustmentValueInputDiagonal';
import { Link } from 'react-router-dom';
import BNInputGroup from '../../components/Group/InputGroup';
import classes from './PricingSettings.module.css';
import { BNExclusionsMultiSelect } from '../../components/Select/ExclusionsMultiSelect';
import { isNumber } from '../../utils/price-utils';
import { SplitsButtonGroup } from '../../hoc/SeatingChart/Filters/Filters.SplitsButtonGroup';
import { usePricingSettings } from '../../hoc/Settings/PricingSettings.hooks';
import { useGlobalState } from '../../data/Global.state';
import { SoftFloorInput } from '../../components/SoftFloor/SoftFloor';
import adjustmentInputClasses from '../../components/AdjustmentInput/AdjustmentValueInputDiagonal.module.css';
import { getTrailingFloorDisplayValue, getTrailingFloorRuleValue } from '../../utils/trailing-floor';

export default PricingSettings;

const getDefaultSoftFloor = () => ({
  value: 0,
  typeId: BrokerNerdsBarkerCoreEnumsAdjustmentType.Percentage,
  period: '1.00:00:00',
});

export function PricingSettings() {
  const { form, submit, cancel } = usePricingSettings('form', 'submit', 'cancel');
  const { tenants } = useGlobalState('tenants');
  const isMultiTenantUser = (tenants?.length ?? 0) > 1;

  const isTicketNetworkMarketplaceEnabled = useFlag('ticketnetwork-marketplace');
  const hasSoftFloorSettingFlag = useFlag('soft-floor-setting');
  const hasTrailingFloorSettingFlag = useFlag('trailing-floor');

  const [scheduleIntervalPeriod, setScheduleIntervalPeriod] = useState<'Min' | 'Hr' | 'Day'>(
    (form.values.schedulePricerSettings?.intervalMinutes ?? 0) % 1440 === 0 ? 'Day' : (form.values.schedulePricerSettings?.intervalMinutes ?? 0) % 60 === 0 ? 'Hr' : 'Min',
  );

  const [scheduleIntervalValue, setScheduleIntervalValue] = useState<number>(
    (form.values.schedulePricerSettings?.intervalMinutes ?? 0) % 1440 === 0
      ? (form.values.schedulePricerSettings?.intervalMinutes ?? 0) / 1440
      : (form.values.schedulePricerSettings?.intervalMinutes ?? 0) % 60 === 0
        ? (form.values.schedulePricerSettings?.intervalMinutes ?? 0) / 60
        : form.values.schedulePricerSettings?.intervalMinutes ?? 0,
  );
  const trailingFloorDisplayValue = getTrailingFloorDisplayValue(form.values.trailingFloor);
  const trailingFloorType = form.values.trailingFloor?.typeId ?? BrokerNerdsBarkerCoreEnumsAdjustmentType.Amount;
  const isSoftFloorEnabled = !!form.values.softFloor;

  return (
    <>
      <AdminContentHeader
        title="Pricing Settings"
        sticky
        rightSection={
          <>
            <BNButton size="xs" variant="default" disabled={!form.isDirty()} onClick={() => cancel()}>
              Cancel
            </BNButton>
            <BNButton size="xs" variant="filled" color="green" disabled={!form.isDirty()} onClick={() => submit()}>
              Save
            </BNButton>
          </>
        }
      />
      <Box pt="xl" pb={0}>
        <Title order={2} size="h4" pt="sm" mb="lg">
          Search Defaults
        </Title>
        <Box>
          <div className={classes.searchRangeWrapper}>
            <BNInputGroup label={form.values.searchRange.startsWith('c') ? 'Search Range (# of days)' : 'Search Range'}>
              <BNInputGroup.Item w={140}>
                <BNSelect
                  size="xs"
                  style={{ flex: 1 }}
                  miw={120}
                  label={form.values.searchRange.startsWith('c') ? 'Search Range (# of days)' : 'Search Range'}
                  data={[
                    { value: 'd1', label: 'Today' },
                    { value: 'w1', label: '1 Week' },
                    { value: 'M1', label: '1 Month' },
                    { value: 'y1', label: '1 Year' },
                    { value: 'all', label: 'All' },
                    { value: 'c', label: 'Custom' },
                  ]}
                  {...form.getInputProps('searchRange')}
                />
              </BNInputGroup.Item>
              {form.values.searchRange.startsWith('c') && (
                <BNInputGroup.Item w={140}>
                  <BNNumberInput size="xs" min={0} placeholder="# of Days" {...form.getInputProps('searchRangeCustom')} />
                </BNInputGroup.Item>
              )}
            </BNInputGroup>
          </div>
        </Box>
      </Box>
      <Box py="xl" pb="lg">
        <Box pb="lg">
          <Title order={2} size="h4" pt="sm">
            Pricing Mode
          </Title>
          <Text size="xs" mb="lg" c="var(--colors-gray-5)">
            Pricing mode allow for immediate price changes or setting multiple prices in a pending state before applying them to the listings.
          </Text>
          <Box>
            <div style={{ maxWidth: 280 }}>
              <BNSelect
                size="xs"
                label="Pricing Mode"
                data={[
                  { value: BrokerNerdsBarkerCoreEnumsPricingMode.Individual, label: BrokerNerdsBarkerCoreEnumsPricingMode.Individual },
                  { value: BrokerNerdsBarkerCoreEnumsPricingMode.Staged, label: BrokerNerdsBarkerCoreEnumsPricingMode.Staged },
                ]}
                value={form.values.pricingMode}
                onChange={(e) => form.setFieldValue('pricingMode', e! as BrokerNerdsBarkerCoreEnumsPricingMode)}
              />
            </div>
            <Text fz={11} mt={4} c="gray.5">
              {form.values.pricingMode === BrokerNerdsBarkerCoreEnumsPricingMode.Individual && 'You must save your changes to each listing as you price them.'}
              {form.values.pricingMode === BrokerNerdsBarkerCoreEnumsPricingMode.Staged && 'You can set multiple prices in a pending state before applying them to the listings.'}
            </Text>
          </Box>
        </Box>
        <Box pb="lg">
          <Title order={2} size="h4" pt="sm">
            Marketplace Defaults
          </Title>
          <Text size="xs" mb="lg" c="var(--colors-gray-5)">
            These settings will be used as defaults for the pricer and can be overridden for each listing.
          </Text>
          <Box>
            <div style={{ maxWidth: 280 }}>
              <BNSelect
                size="xs"
                label="Default Marketplace"
                data={
                  isTicketNetworkMarketplaceEnabled
                    ? [
                        { value: BrokerNerdsBarkerCoreEnumsMarketplace.TicketEvolution, label: 'Ticket Evolution' },
                        { value: BrokerNerdsBarkerCoreEnumsMarketplace.SeatGeek, label: 'SeatGeek' },
                        { value: BrokerNerdsBarkerCoreEnumsMarketplace.TicketNetwork, label: 'TicketNetwork' },
                        { value: BrokerNerdsBarkerCoreEnumsMarketplace.Ticketmaster, label: 'Ticketmaster' },
                        { value: BrokerNerdsBarkerCoreEnumsMarketplace.Ticombo, label: 'Ticombo' },
                      ]
                    : [
                        { value: BrokerNerdsBarkerCoreEnumsMarketplace.TicketEvolution, label: 'Ticket Evolution' },
                        { value: BrokerNerdsBarkerCoreEnumsMarketplace.SeatGeek, label: 'SeatGeek' },
                        { value: BrokerNerdsBarkerCoreEnumsMarketplace.Ticketmaster, label: 'Ticketmaster' },
                        { value: BrokerNerdsBarkerCoreEnumsMarketplace.Ticombo, label: 'Ticombo' },
                      ]
                }
                value={form.values.defaultMarketplace}
                onChange={(e) => form.setFieldValue('defaultMarketplace', e! as BrokerNerdsBarkerCoreEnumsMarketplace)}
              />
            </div>
          </Box>
        </Box>
        <Box pb="xl">
          <Title order={3} size="h5" mt="xs">
            Rows
          </Title>
          <Text size="xs" mb="xs" c="var(--colors-gray-5)">
            Based on your listing&apos;s row, the pricer can decide which rows to compare against.
          </Text>
          {form.values.rowComparisons.map((rowComparison, index) => (
            <Group mb="md" gap={12} align="end" key={index}>
              <div>
                <BNTextInput label="Row Range" w={132} size="xs" placeholder="1-10" {...form.getInputProps(`rowComparisons.${index}.rowRange`)} />
              </div>
              <div>
                <BNNumberInput label="+/- Rows" w={132} size="xs" placeholder="5" {...form.getInputProps(`rowComparisons.${index}.threshold`)} />
              </div>
              <BNButton
                variant="light"
                size="xs"
                onClick={() => {
                  form.removeListItem('rowComparisons', index);
                }}
              >
                Remove
              </BNButton>
            </Group>
          ))}
          <BNButton variant="default" size="xs" onClick={() => form.insertListItem('rowComparisons', { rowRange: '', threshold: 0 })}>
            Add a Row Range
          </BNButton>
        </Box>
        <Box pb="xl">
          <Title order={3} size="h5" mt="xs">
            Splits
          </Title>
          <Text size="xs" mb="xs" c="var(--colors-gray-5)">
            Select the splits you would like to include by default for specific ticket quantities.
          </Text>
          <Box maw={280}>
            <Group wrap="nowrap" gap={0}>
              <Text w={44} size="xs" c="var(--colors-gray-5)">
                Qty
              </Text>
              <Text size="xs" c="var(--colors-gray-5)">
                Splits
              </Text>
            </Group>
            <Group wrap="nowrap" my="xs" gap="xs">
              <Text size="sm" w={40} pl="xs">
                1
              </Text>
              <Box w="100%">
                <SplitsButtonGroup
                  splits={form.values.quantitySplitMatrix && form.values.quantitySplitMatrix['1']}
                  disabled={form.values.autoAdjustSplits}
                  onChange={(splits) => {
                    form.setFieldValue('quantitySplitMatrix', {
                      ...form.values.quantitySplitMatrix,
                      1: splits,
                    });
                  }}
                  size="xs"
                />
              </Box>
            </Group>
            <Group wrap="nowrap" my="xs" gap="xs">
              <Text size="sm" w={40} pl="xs">
                2
              </Text>
              <SplitsButtonGroup
                splits={form.values.quantitySplitMatrix && form.values.quantitySplitMatrix['2']}
                disabled={form.values.autoAdjustSplits}
                onChange={(splits) => {
                  form.setFieldValue('quantitySplitMatrix', {
                    ...form.values.quantitySplitMatrix,
                    2: splits,
                  });
                }}
                size="xs"
              />
            </Group>
            <Group wrap="nowrap" my="xs" gap="xs">
              <Text size="sm" w={40} pl="xs">
                3
              </Text>
              <SplitsButtonGroup
                splits={form.values.quantitySplitMatrix && form.values.quantitySplitMatrix['3']}
                disabled={form.values.autoAdjustSplits}
                onChange={(splits) => {
                  form.setFieldValue('quantitySplitMatrix', {
                    ...form.values.quantitySplitMatrix,
                    3: splits,
                  });
                }}
                size="xs"
              />
            </Group>
            <Group wrap="nowrap" my="xs" gap="xs">
              <Text size="sm" w={40} pl="xs">
                4
              </Text>
              <SplitsButtonGroup
                splits={form.values.quantitySplitMatrix && form.values.quantitySplitMatrix['4']}
                disabled={form.values.autoAdjustSplits}
                onChange={(splits) => {
                  form.setFieldValue('quantitySplitMatrix', {
                    ...form.values.quantitySplitMatrix,
                    4: splits,
                  });
                }}
                size="xs"
              />
            </Group>
            <Group wrap="nowrap" my="xs" gap="xs">
              <Text size="sm" w={40} pl="xs">
                5
              </Text>
              <SplitsButtonGroup
                splits={form.values.quantitySplitMatrix && form.values.quantitySplitMatrix['5']}
                disabled={form.values.autoAdjustSplits}
                onChange={(splits) => {
                  form.setFieldValue('quantitySplitMatrix', {
                    ...form.values.quantitySplitMatrix,
                    5: splits,
                  });
                }}
                size="xs"
              />
            </Group>
            <Group wrap="nowrap" my="xs" gap="xs">
              <Text size="sm" w={40} pl="xs">
                6
              </Text>
              <SplitsButtonGroup
                splits={form.values.quantitySplitMatrix && form.values.quantitySplitMatrix['6']}
                disabled={form.values.autoAdjustSplits}
                onChange={(splits) => {
                  form.setFieldValue('quantitySplitMatrix', {
                    ...form.values.quantitySplitMatrix,
                    6: splits,
                  });
                }}
                size="xs"
              />
            </Group>
            <Group wrap="nowrap" my="xs" gap="xs">
              <Text size="sm" w={40} pl="xs">
                7
              </Text>
              <SplitsButtonGroup
                splits={form.values.quantitySplitMatrix && form.values.quantitySplitMatrix['7']}
                disabled={form.values.autoAdjustSplits}
                onChange={(splits) => {
                  form.setFieldValue('quantitySplitMatrix', {
                    ...form.values.quantitySplitMatrix,
                    7: splits,
                  });
                }}
                size="xs"
              />
            </Group>
            <Group wrap="nowrap" my="xs" gap="xs">
              <Text size="sm" w={40} pl="xs">
                8+
              </Text>
              <SplitsButtonGroup
                splits={form.values.quantitySplitMatrix && form.values.quantitySplitMatrix['8']}
                disabled={form.values.autoAdjustSplits}
                onChange={(splits) => {
                  form.setFieldValue('quantitySplitMatrix', {
                    ...form.values.quantitySplitMatrix,
                    8: splits,
                  });
                }}
                size="xs"
              />
            </Group>
            <Text size="xs" mb="xs" c="var(--colors-gray-5)" style={{ textWrap: 'nowrap' }}>
              Listings identified as general admission will use the following splits. If none are selected, the pricer will fallback to the appropriate split based on the quantity.
            </Text>
            <Group wrap="nowrap" my="xs" gap="xs">
              <Text size="sm" w={40} pl="xs">
                GA
              </Text>
              <SplitsButtonGroup
                splits={form.values.generalAdmissionSplits}
                onChange={(splits) => {
                  form.setFieldValue('generalAdmissionSplits', splits);
                }}
                size="xs"
              />
            </Group>
            <Text size="xs" mb="xs" c="var(--colors-gray-5)" style={{ textWrap: 'nowrap' }}>
              If your row isn&#39;t numeric, single-letter, double-letter, or triple-letter, the pricer will assume it&#39;s general admission.
            </Text>
          </Box>
          <Box py="xs">
            <BNSwitch className={classes.switchOutline} {...form.getInputProps('autoAdjustSplits', { type: 'checkbox' })} outlined label="Auto-Splits" size="sm" />
            {isMultiTenantUser ? (
              <>
                <Text size="xs" c="var(--colors-gray-5)" py="xs">
                  Let the auto-pricer maintain your splits based on the quantity of tickets in the listing.
                </Text>
                <Text maw={480} component="p" size="xs" c="var(--colors-gray-5)" my={0} bg="var(--colors-paperHover)" p="xs" px="sm" style={{ borderRadius: 3 }}>
                  Auto-splits are based on company-wide settings. To adjust these settings you must select a company from the{' '}
                  <Link to="/companies">
                    <Text component="span" td="underline" c="var(--colors-brandcolor-5)">
                      Companies
                    </Text>
                  </Link>{' '}
                  page.
                </Text>
              </>
            ) : (
              <>
                <Text size="xs" c="var(--colors-gray-5)" py="xs">
                  Let the auto-pricer maintain your splits based on the quantity of tickets in the listings.
                </Text>
                <Text maw={480} component="p" size="xs" c="var(--colors-gray-5)" my={0} bg="var(--colors-paperHover)" p="xs" px="sm" style={{ borderRadius: 3 }}>
                  Auto-splits are based on company-wide settings available in the{' '}
                  <Link to={`/companies/${tenants?.[0].tenantId}/settings/pricing`}>
                    <Text component="span" td="underline" c="var(--colors-brandcolor-5)">
                      Company Settings
                    </Text>
                  </Link>{' '}
                  page.
                </Text>
              </>
            )}
          </Box>
        </Box>
        <Box pb="xl" style={{ maxWidth: 280 }}>
          <Title order={3} size="h5">
            Exclusions
          </Title>
          <Text size="xs" c="var(--colors-gray-5)" mb="xs">
            Exclude listings by default with these traits.
          </Text>
          <BNExclusionsMultiSelect
            removeSelected
            value={((form.values.exclusions ?? []) as (BrokerNerdsBarkerCoreEnumsListingDisclosure | BrokerNerdsBarkerCoreEnumsDeliveryMethod)[]).concat(
              form.values.deliveryExclusions ?? [],
            )}
            placeholder="None"
            onChange={(value: (BrokerNerdsBarkerCoreEnumsListingDisclosure | BrokerNerdsBarkerCoreEnumsDeliveryMethod | string)[]) => {
              const deliveryExclusions = value.filter((v) =>
                Object.values(BrokerNerdsBarkerCoreEnumsDeliveryMethod).includes(v as unknown as BrokerNerdsBarkerCoreEnumsDeliveryMethod),
              ) as BrokerNerdsBarkerCoreEnumsDeliveryMethod[];
              const disclosureExclusions = value.filter((v) =>
                Object.values(BrokerNerdsBarkerCoreEnumsListingDisclosure).includes(v as unknown as BrokerNerdsBarkerCoreEnumsListingDisclosure),
              ) as BrokerNerdsBarkerCoreEnumsListingDisclosure[];

              form.setFieldValue('exclusions', disclosureExclusions);
              form.setFieldValue('deliveryExclusions', deliveryExclusions);
            }}
          />
        </Box>
        <Box style={{ maxWidth: 280 }}>
          <Title order={3} size="h5">
            Outliers
          </Title>
          <Text size="xs" c="var(--colors-gray-5)" mb="xs">
            Exclude outlier listings based on their percentage below the market.
          </Text>
          <SimpleGrid cols={2}>
            <BNSelect
              clearable
              data={[
                { value: '2', label: '2nd Position' },
                { value: '3', label: '3rd Position' },
                { value: '4', label: '4th Position' },
                { value: '5', label: '5th Position' },
                { value: '6', label: '6th Position' },
              ]}
              label="Compare to"
              size="xs"
              placeholder="Select a position"
              onChange={(value) => {
                if (!value) {
                  form.setFieldValue('outlierCriteria.percentUnder', 0);
                  form.setFieldValue('outlierCriteria.comparableIndex', 0);
                } else if (isNumber(value)) {
                  form.setFieldValue('outlierCriteria.comparableIndex', parseInt(value));
                }
              }}
              error={form.errors['outlierCriteria.comparableIndex']}
              value={(form.values.outlierCriteria?.comparableIndex ?? 0) > 0 ? form.values.outlierCriteria?.comparableIndex.toString() : null}
              className={classes.outlierInput}
            />
            <Stack gap={1} justify="end">
              <Text component="label" c={form.errors['outlierCriteria.percentUnder'] ? 'var(--colors-red-5)' : 'var(--colors-gray-5)'} size="xs" lh="1.25rem">
                Percentage Under
              </Text>
              <Group gap="xs" wrap="nowrap" pos="relative">
                <Slider size="sm" min={0} max={20} step={1} label={null} w={220} className={classes.pctSlider} {...form.getInputProps('outlierCriteria.percentUnder')} />
                <BNNumberInput
                  label=""
                  size="xs"
                  min={0}
                  max={20}
                  hideControls
                  w={240}
                  fixedDecimalScale
                  decimalScale={0}
                  className={classes.pctInput}
                  selectOnFocus
                  rightSection={
                    <Text size="xs" c="var(--colors-gray-4)">
                      %
                    </Text>
                  }
                  rightSectionWidth={24}
                  error={form.errors['outlierCriteria.percentUnder']}
                  {...form.getInputProps('outlierCriteria.percentUnder')}
                />
              </Group>
            </Stack>
          </SimpleGrid>
        </Box>
        {(hasSoftFloorSettingFlag || hasTrailingFloorSettingFlag) && (
          <>
            <Divider color="var(--colors-gray-1)" variant="dotted" my="xl" />
            <Box style={{ maxWidth: 600 }}>
              <Title order={2} size="h4">
                Advanced Feature Toggles
              </Title>
              <Text size="xs" c="var(--colors-gray-5)">
                These features will unlock more advanced functionality within the pricer. Please consult the FAQ and use with caution.
              </Text>
              {hasSoftFloorSettingFlag && (
                <Box py="xs">
                  <BNSwitch
                    size="sm"
                    label="Soft Floors"
                    {...form.getInputProps('featureFlags.softFloor', { type: 'checkbox' })}
                    onChange={(e) => {
                      form.setFieldValue('featureFlags.softFloor', e.currentTarget.checked);
                      if (!e.currentTarget.checked) {
                        form.setFieldValue('softFloor', undefined);
                      } else if (!form.values.softFloor) {
                        form.setFieldValue('softFloor', getDefaultSoftFloor());
                      }
                    }}
                  />
                  <Text size="xs" c="var(--colors-gray-5)" py="xs">
                    Define criteria for how far a listing can drop in price over a period of time.
                  </Text>
                </Box>
              )}
              {hasTrailingFloorSettingFlag && (
                <Box py="xs">
                  <BNSwitch
                    size="sm"
                    label="Trailing Floors"
                    {...form.getInputProps('featureFlags.trailingFloor', { type: 'checkbox' })}
                    onChange={(e) => {
                      const { checked } = e.currentTarget;
                      form.setFieldValue('featureFlags.trailingFloor', checked);
                    }}
                  />
                  <Text size="xs" c="var(--colors-gray-5)" py="xs">
                    Program the pricer to raise your floors when the market trends up.
                  </Text>
                </Box>
              )}
            </Box>
          </>
        )}
        <Divider color="var(--colors-gray-1)" variant="dotted" my="xl" />
        <Box>
          <Title order={2} size="h4">
            Automation
          </Title>
          <Text size="xs" c="var(--colors-gray-5)">
            These settings will be used as defaults for the auto-pricer and scheduled pricer and can be overridden for each listing.
          </Text>
          <Box py="xs">
            <BNSwitch outlined className={classes.switchOutline} {...form.getInputProps('broadcastSingleListing', { type: 'checkbox' })} label="Auto-Broadcast" size="sm" />
            <Text size="xs" c="var(--colors-gray-5)" py="xs">
              Automatically broadcast listings any time you enable auto-pricing.
            </Text>
          </Box>
        </Box>
        <Divider color="var(--colors-gray-1)" variant="dotted" my="xl" />
        <Box>
          <Title order={2} size="h5">
            Auto-Pricing Defaults
          </Title>
          <Text size="xs" c="var(--colors-gray-5)">
            These settings will be used as defaults for the auto-pricer and can be overridden for each listing.
          </Text>
          <Group w={300} gap={12}>
            <Box py="xs" w={134}>
              <AdjustmentValueInputDiagonal
                label="Adjustment"
                onChange={(value) => {
                  form.getInputProps('adjustmentValue').onChange(value.value);
                  form.getInputProps('adjustmentTypeId').onChange(value.type);
                }}
                value={form.values.adjustmentValue || 0}
                type={form.values.adjustmentTypeId || 'Amount'}
                disabled={false}
                numInputWidth={83}
              />
            </Box>
            <Box py="xs" w={134}>
              <BNNumberInput
                label="# Comparables"
                size="xs"
                min={1}
                selectOnFocus
                value={form.values.numComparables?.[0].numComparables}
                onChange={(value) => form.setFieldValue('numComparables.0.numComparables', value === '' ? undefined : value)}
              />
            </Box>
          </Group>
          {hasTrailingFloorSettingFlag && (
            <Box py="xs">
              <BNSwitch
                outlined
                className={classes.switchOutline}
                checked={form.values.useTrailingFloor}
                label="Use Trailing Floor"
                size="sm"
                onChange={(e) => {
                  const { checked } = e.currentTarget;
                  form.setFieldValue('useTrailingFloor', checked);

                  if (!checked) {
                    form.setFieldValue('trailingFloor', undefined);
                    return;
                  }

                  if (!form.values.trailingFloor) {
                    form.setFieldValue('trailingFloor', {
                      typeId: BrokerNerdsBarkerCoreEnumsAdjustmentType.Amount,
                      value: -1,
                    });
                  }
                }}
              />
            </Box>
          )}
          {form.values.useTrailingFloor && (
            <Group w={134}>
              <Box py="xs" w={134}>
                <BNInputGroup label="Trailing Floor" error={form.errors['trailingFloor.value']}>
                  <BNInputGroup.Item w={36}>
                    <BNSelect
                      className={`${adjustmentInputClasses.middleInput} ${adjustmentInputClasses.shortSelectRoot}`}
                      size="xs"
                      value={trailingFloorType}
                      dropdownWidth={40}
                      rightSectionWidth={20}
                      comboboxProps={{ styles: { option: { paddingLeft: 4, paddingRight: 4, display: 'flex', justifyContent: 'center' } } }}
                      onChange={(value) => {
                        if (!value) {
                          return;
                        }
                        const nextType = value as BrokerNerdsBarkerCoreEnumsAdjustmentType;
                        form.setFieldValue('trailingFloor', {
                          typeId: nextType,
                          value: getTrailingFloorRuleValue(trailingFloorDisplayValue, nextType),
                        });
                      }}
                      data={[
                        { value: BrokerNerdsBarkerCoreEnumsAdjustmentType.Amount, label: '$' },
                        { value: BrokerNerdsBarkerCoreEnumsAdjustmentType.Percentage, label: '%' },
                      ]}
                      styles={{
                        input: {
                          paddingLeft: 6,
                          paddingRight: 0,
                          borderTopRightRadius: 0,
                          borderBottomRightRadius: 0,
                        },
                      }}
                    />
                  </BNInputGroup.Item>
                  <BNInputGroup.Item w={98}>
                    <BNNumberInput
                      selectOnFocus
                      value={trailingFloorDisplayValue}
                      onChange={(value) => {
                        const displayValue = typeof value === 'string' || value === undefined ? 0 : value;
                        form.setFieldValue('trailingFloor', {
                          typeId: trailingFloorType,
                          value: getTrailingFloorRuleValue(displayValue, trailingFloorType),
                        });
                      }}
                      allowNegative
                      decimalScale={2}
                      fixedDecimalScale
                      step={1}
                      stepHoldDelay={500}
                      stepHoldInterval={100}
                      max={0}
                      size="xs"
                      styles={{
                        input: {
                          borderTopLeftRadius: 0,
                          borderBottomLeftRadius: 0,
                        },
                      }}
                    />
                  </BNInputGroup.Item>
                </BNInputGroup>
              </Box>
            </Group>
          )}
          {!form.values.useTrailingFloor && (
            <>
              <Group w={300} gap={12}>
                <Box py="xs" w={134}>
                  <BNNumberInput label="Floor" size="xs" step={1} min={0} decimalScale={2} fixedDecimalScale selectOnFocus {...form.getInputProps('floorMultiplier')} />
                  <Text fz={11} mt={4} c="gray.5">
                    % of lowest comparable
                  </Text>
                </Box>
                <Box py="xs" w={134}>
                  <BNNumberInput label="Ceiling" size="xs" step={1} min={0} decimalScale={2} fixedDecimalScale selectOnFocus {...form.getInputProps('ceilingMultiplier')} />
                  <Text fz={11} mt={4} c="gray.5">
                    % of lowest comparable
                  </Text>
                </Box>
              </Group>
              <Group w={300} gap={12}>
                <Box py="xs" w={134}>
                  <BNNumberInput label="Floor" size="xs" step={1} min={0} decimalScale={2} fixedDecimalScale selectOnFocus {...form.getInputProps('floorCostMultiplier')} />
                  <Text fz={11} mt={4} c="gray.5">
                    % of listing cost
                  </Text>
                </Box>
                <Box py="xs" w={134}>
                  <BNNumberInput label="Ceiling" size="xs" step={1} min={0} decimalScale={2} fixedDecimalScale selectOnFocus {...form.getInputProps('ceilingCostMultiplier')} />
                  <Text fz={11} mt={4} c="gray.5">
                    % of listing cost
                  </Text>
                </Box>
              </Group>
              {form.values.featureFlags.softFloor && (
                <Box py="xs">
                  <BNSwitch
                    outlined
                    className={classes.switchOutline}
                    checked={isSoftFloorEnabled}
                    label="Use Soft Floor"
                    size="sm"
                    onChange={(e) => {
                      const { checked } = e.currentTarget;

                      if (checked) {
                        if (!form.values.softFloor) {
                          form.setFieldValue('softFloor', getDefaultSoftFloor());
                        }
                        return;
                      }

                      form.setFieldValue('softFloor', undefined);
                    }}
                  />
                </Box>
              )}
            </>
          )}
          {form.values.featureFlags.softFloor && !form.values.useTrailingFloor && isSoftFloorEnabled && (
            <Group w={300}>
              <Box py="xs" w={280}>
                <SoftFloorInput
                  min={0}
                  softFloor={form.values.softFloor}
                  onFloorChange={(value) => {
                    form.setValues({
                      ...form.values,
                      softFloor: {
                        ...form.values.softFloor,
                        value: value.value ?? 0,
                        typeId: value.type,
                        period: form.values.softFloor?.period ?? '1.00:00:00',
                      },
                    });
                  }}
                  onPeriodChange={(value) => {
                    form.setFieldValue('softFloor.period', value);
                  }}
                />
              </Box>
            </Group>
          )}
          <Group w={300} gap={12}>
            <Box py="xs" w={134}>
              <Box pos="relative">
                <Text size="xs" component="label" c="var(--colors-gray-5)" lh="1.25rem">
                  # Active
                </Text>
                <Tooltip withArrow label="Broadcast all listings in the group">
                  <Box pos="absolute" top={6} right={0}>
                    <BNSwitch
                      checked={form.values.numActive >= 2147483647}
                      onChange={() => form.setFieldValue('numActive', form.values.numActive >= 2147483647 ? 1 : 2147483647)}
                      size="xxs"
                      label="ALL"
                    />
                  </Box>
                </Tooltip>
                {form.values.numActive >= 2147483647 && <BNTextInput disabled size="xs" value="All Listings" />}
                {form.values.numActive < 2147483647 && (
                  <BNNumberInput {...form.getInputProps('numActive')} decimalScale={0} fixedDecimalScale allowDecimal={false} min={0} step={1} size="xs" />
                )}
              </Box>
            </Box>
            <Box py="xs" w={134}>
              <StaggerByValueInput
                numInputWidth={83}
                disabled={false}
                label="Stagger By"
                onChange={(value) => {
                  form.getInputProps('staggerByValue').onChange(value.value);
                  form.getInputProps('staggerByTypeId').onChange(value.type);
                }}
                value={form.values.staggerByValue || 0}
                type={form.values.staggerByTypeId || 'Amount'}
              />
            </Box>
          </Group>
        </Box>
        <Divider color="var(--colors-gray-1)" my="xl" />
        <Box>
          <Title order={2} size="h5">
            Scheduled Pricing Defaults
          </Title>
          <Text size="xs" c="var(--colors-gray-5)">
            These settings will be used as defaults for the scheduled pricer and can be overridden for each listing.
          </Text>
          <Group w={300} gap={12}>
            <Box py="xs" w={134}>
              <AdjustmentValueInputDiagonal
                label="Adjustment"
                onChange={(value) => {
                  form.getInputProps('schedulePricerSettings.adjustmentValue').onChange(value.value);
                  form.getInputProps('schedulePricerSettings.adjustmentTypeId').onChange(value.type);
                }}
                value={form.values.schedulePricerSettings?.adjustmentValue || 0}
                type={form.values.schedulePricerSettings?.adjustmentTypeId || 'Amount'}
                disabled={false}
                numInputWidth={83}
              />
            </Box>
            <Box py="xs" w={134}>
              <BNInputGroup label="Interval" error={form.errors['schedulePricerSettings.intervalMinutes']}>
                <BNInputGroup.Item>
                  <BNNumberInput
                    decimalScale={0}
                    step={1}
                    min={0}
                    max={scheduleIntervalPeriod === 'Min' ? 14400 : scheduleIntervalPeriod === 'Hr' ? 240 : 10}
                    size="xs"
                    label="Interval"
                    value={scheduleIntervalValue}
                    onChange={(e) => {
                      if (e === '') {
                        setScheduleIntervalValue(0);
                        form.setFieldValue('schedulePricerSettings.intervalMinutes', 0);
                      } else {
                        const val = Math.min(e, scheduleIntervalPeriod === 'Min' ? 14400 : scheduleIntervalPeriod === 'Hr' ? 240 : 10);
                        setScheduleIntervalValue(val);
                        form.setFieldValue('schedulePricerSettings.intervalMinutes', val * (scheduleIntervalPeriod === 'Min' ? 1 : scheduleIntervalPeriod === 'Hr' ? 60 : 1440));
                      }
                    }}
                  />
                </BNInputGroup.Item>
                <BNInputGroup.Item>
                  <BNSelect
                    data={['Min', 'Hr', 'Day']}
                    size="xs"
                    value={scheduleIntervalPeriod}
                    aria-label="Interval Period"
                    onChange={(e) => {
                      if (e === 'Min' || e === 'Hr' || e === 'Day') {
                        setScheduleIntervalPeriod(e);
                        form.setFieldValue('schedulePricerSettings.intervalMinutes', scheduleIntervalValue * (e === 'Min' ? 1 : e === 'Hr' ? 60 : 1440));
                      }
                    }}
                  />
                </BNInputGroup.Item>
              </BNInputGroup>
            </Box>
          </Group>
          <Group w={300} gap={12}>
            <Box py="xs" w={134}>
              <BNNumberInput
                label="Floor"
                size="xs"
                step={1}
                min={0}
                decimalScale={2}
                fixedDecimalScale
                selectOnFocus
                {...form.getInputProps('schedulePricerSettings.floorMultiplier')}
              />
              <Text fz={11} mt={4} c="var(--colors-gray-5)">
                % of cost
              </Text>
            </Box>
            <Box py="xs" w={134}>
              <BNNumberInput
                label="Ceiling"
                size="xs"
                step={1}
                min={0}
                decimalScale={2}
                fixedDecimalScale
                selectOnFocus
                {...form.getInputProps('schedulePricerSettings.ceilingMultiplier')}
              />
              <Text fz={11} mt={4} c="var(--colors-gray-5)">
                % of cost
              </Text>
            </Box>
          </Group>
          <Group w={300} gap={12}>
            <Box py="xs" w={134}>
              <Box pos="relative">
                <Text size="xs" c="var(--colors-gray-5)" component="label" lh="1.25rem">
                  # Active
                </Text>
                <Tooltip withArrow label="Broadcast all listings in the group">
                  <Box pos="absolute" top={6} right={0}>
                    <BNSwitch
                      checked={form.values.schedulePricerSettings.numActive >= 2147483647}
                      onChange={() => form.setFieldValue('schedulePricerSettings.numActive', form.values.schedulePricerSettings.numActive >= 2147483647 ? 1 : 2147483647)}
                      size="xxs"
                      label="ALL"
                    />
                  </Box>
                </Tooltip>
                {form.values.schedulePricerSettings.numActive >= 2147483647 && <BNTextInput disabled size="xs" value="All Listings" />}
                {form.values.schedulePricerSettings.numActive < 2147483647 && (
                  <BNNumberInput {...form.getInputProps('schedulePricerSettings.numActive')} decimalScale={0} fixedDecimalScale allowDecimal={false} min={0} step={1} size="xs" />
                )}
              </Box>
            </Box>
            <Box py="xs" w={134}>
              <StaggerByValueInput
                numInputWidth={83}
                disabled={false}
                label="Stagger By"
                onChange={(value) => {
                  form.getInputProps('schedulePricerSettings.staggerByValue').onChange(value.value);
                  form.getInputProps('schedulePricerSettings.staggerByTypeId').onChange(value.type);
                }}
                value={form.values.schedulePricerSettings?.staggerByValue || 0}
                type={form.values.schedulePricerSettings?.staggerByTypeId || 'Amount'}
              />
            </Box>
          </Group>
        </Box>
      </Box>
    </>
  );
}
